import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { featuredTools } from '../data/featuredTools';
import { Tool } from '../data/types';
import { 
  ExternalLink, 
  ArrowLeft, 
  Star, 
  Tag, 
  DollarSign, 
  Zap,
  Crown,
  Bookmark,
  Share2,
  ChevronRight,
  Award,
  Users,
  TrendingUp,
  CheckCircle,
  Clock,
  Shield,
  Rocket
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import slugify from '../utils/slugify';

// Constants
const TOOL_STATS = {
  rating: "4.8/5",
  reviews: "250+",
  status: "Featured",
  pricing: {
    free: "Free Available",
    paid: "Premium Tool"
  }
} as const;

const FeaturedToolDetailPage: React.FC = () => {
  const { toolId } = useParams<{ toolId: string }>();
  const [tool, setTool] = useState<Tool | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);

  // Find tool based on slug
  useEffect(() => {
    if (toolId) {
      const foundTool = featuredTools.find(t => slugify(t.title) === toolId);
      setTool(foundTool ?? null);
    }
  }, [toolId]);

  // Memoized SEO data
  const seoData = useMemo(() => {
    if (!tool) return null;

    const baseTitle = `${tool.title} - Featured AI Tool | Expert Review & Analysis | TooliQ.ai`;
    const baseDescription = `${tool.description} Discover why ${tool.title} is a featured AI tool on TooliQ.ai. Expert analysis, key features, pricing, and user reviews.`;
    const keywords = `${tool.tags?.join(', ')}, featured AI tools, ${tool.title} review, best AI tools, TooliQ.ai featured`;
    const canonical = `https://tooliq.ai/tools/featured/${slugify(tool.title)}`;

    return { baseTitle, baseDescription, keywords, canonical };
  }, [tool]);

  // Event handlers
  const handleShare = useCallback(async () => {
    if (!tool) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${tool.title} | Featured AI Tool | TooliQ.ai`,
          text: tool.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Add toast notification here
    }
  }, [tool]);

  const toggleBookmark = useCallback(() => {
    setIsBookmarked(prev => !prev);
  }, []);

  // Loading/error state
  if (!tool) {
    return (
      <ToolNotFound />
    );
  }

  // Generate OG image URL
  const ogImage = `https://tooliq.ai/og-images/${slugify(tool.title)}.webp`;

  return (
    <>
      <Helmet>
        <title>{seoData?.baseTitle}</title>
        <meta name="description" content={seoData?.baseDescription} />
        <meta name="keywords" content={seoData?.keywords} />
        <link rel="canonical" href={seoData?.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={seoData?.baseTitle} />
        <meta property="og:description" content={seoData?.baseDescription} />
        <meta property="og:url" content={seoData?.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData?.baseTitle} />
        <meta name="twitter:description" content={seoData?.baseDescription} />
        <meta name="twitter:image" content={ogImage} />

        {/* JSON-LD for Featured Tool */}
        <script type="application/ld+json">
          {JSON.stringify(getStructuredData(tool, seoData))}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-indigo-900/20">
        {/* Featured Badge */}
        <FeaturedBadge />

        {/* Breadcrumb */}
        <Breadcrumb toolTitle={tool.title} />

        {/* Main Content */}
        <MainContent 
          tool={tool}
          isBookmarked={isBookmarked}
          onBookmarkToggle={toggleBookmark}
          onShare={handleShare}
        />
      </div>
    </>
  );
};

// Helper function for structured data
const getStructuredData = (tool: Tool, seoData: any) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": tool.title,
  "description": tool.description,
  "applicationCategory": "AI Tool",
  "operatingSystem": "All",
  "offers": {
    "@type": "Offer",
    "price": tool.isFree ? "0" : "Varies",
    "priceCurrency": "USD"
  },
  "url": tool.toolUrl || seoData?.canonical,
  "author": {
    "@type": "Organization",
    "name": "TooliQ.ai",
    "url": "https://tooliq.ai"
  },
  "award": "Featured Tool on TooliQ.ai",
  "review": {
    "@type": "Review",
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "4.8",
      "bestRating": "5"
    },
    "author": {
      "@type": "Organization",
      "name": "TooliQ.ai Experts"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "250"
  }
});

// Extracted ToolNotFound component
const ToolNotFound: React.FC = () => (
  <>
    <Helmet>
      <title>Tool Not Found | TooliQ.ai</title>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-24 h-24 bg-purple-100 dark:bg-purple-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <Crown className="w-12 h-12 text-purple-500 dark:text-purple-400" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
          Featured Tool Not Found
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md">
          This featured AI tool doesn't exist or may have been removed.
        </p>
        <Link
          to="/tools/featured"
          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Browse Featured Tools
        </Link>
      </div>
    </div>
  </>
);

// Extracted FeaturedBadge component
const FeaturedBadge: React.FC = () => (
  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex items-center justify-center gap-2 text-sm font-medium">
        <Crown className="w-4 h-4" />
        <span>Featured AI Tool</span>
        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        <span>Curated by TooliQ.ai Experts</span>
      </div>
    </div>
  </div>
);

// Extracted Breadcrumb component
interface BreadcrumbProps {
  toolTitle: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ toolTitle }) => (
  <div className="max-w-7xl mx-auto px-6 py-6">
    <nav className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-6">
      <Link to="/" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
        Home
      </Link>
      <ChevronRight className="w-4 h-4" />
      <Link to="/tools" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
        AI Tools
      </Link>
      <ChevronRight className="w-4 h-4" />
      <Link to="/tools/featured" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
        Featured Tools
      </Link>
      <ChevronRight className="w-4 h-4" />
      <span className="text-slate-800 dark:text-slate-200 font-medium">{toolTitle}</span>
    </nav>
  </div>
);

// Extracted MainContent component
interface MainContentProps {
  tool: Tool;
  isBookmarked: boolean;
  onBookmarkToggle: () => void;
  onShare: () => void;
}

const MainContent: React.FC<MainContentProps> = ({
  tool,
  isBookmarked,
  onBookmarkToggle,
  onShare
}) => (
  <div className="max-w-7xl mx-auto px-6 pb-12">
    <div className="grid lg:grid-cols-3 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-8">
        <HeaderCard 
          tool={tool}
          isBookmarked={isBookmarked}
          onBookmarkToggle={onBookmarkToggle}
          onShare={onShare}
        />
        
        <WhyFeaturedSection />
        
        {tool.longDescription && <LongDescriptionSection tool={tool} />}
        
        <KeyBenefitsSection />
        
        {tool.tags && tool.tags.length > 0 && <TagsSection tags={tool.tags} />}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        <PremiumCTACard tool={tool} />
        <ToolStatsCard tool={tool} />
        <NavigationCards />
      </div>
    </div>
  </div>
);

// Extracted HeaderCard component
interface HeaderCardProps {
  tool: Tool;
  isBookmarked: boolean;
  onBookmarkToggle: () => void;
  onShare: () => void;
}

const HeaderCard: React.FC<HeaderCardProps> = ({
  tool,
  isBookmarked,
  onBookmarkToggle,
  onShare
}) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-purple-200 dark:border-purple-800 p-8 relative overflow-hidden">
    {/* Featured Gradient Overlay */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full blur-3xl opacity-10"></div>
    
    <div className="flex flex-col sm:flex-row sm:items-start gap-6 relative z-10">
      {tool.icon && (
        <div className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center text-4xl text-white shadow-lg">
          {tool.icon}
        </div>
      )}
      
      <div className="flex-1">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-3 py-1 rounded-full text-sm font-medium">
                <Crown className="w-3 h-3" />
                Featured Tool
              </div>
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                tool.isFree 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
              }`}>
                <DollarSign className="w-3 h-3" />
                {tool.isFree ? 'Free' : 'Paid'}
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-3">
              {tool.title}
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              {tool.description}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onBookmarkToggle}
              className={`p-3 rounded-xl border transition-all duration-200 ${
                isBookmarked
                  ? 'bg-purple-100 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700 text-purple-600 dark:text-purple-400'
                  : 'bg-slate-100 border-slate-300 dark:bg-slate-700 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-purple-50 dark:hover:bg-purple-900/20'
              }`}
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} />
            </button>
            
            <button
              onClick={onShare}
              className="p-3 rounded-xl bg-slate-100 border border-slate-300 dark:bg-slate-700 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200"
              aria-label="Share tool"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Extracted WhyFeaturedSection component
const WhyFeaturedSection: React.FC = () => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-purple-200 dark:border-purple-800 p-8">
    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Award className="w-5 h-5 text-purple-500" />
      Why This Tool Is Featured
    </h2>
    <div className="grid md:grid-cols-2 gap-6">
      <FeatureItem 
        icon={<TrendingUp className="w-5 h-5 text-green-500" />}
        title="Proven Performance"
        description="Consistently delivers outstanding results and user satisfaction"
      />
      <FeatureItem 
        icon={<Users className="w-5 h-5 text-blue-500" />}
        title="Community Favorite"
        description="Highly rated and widely adopted by our user community"
      />
      <FeatureItem 
        icon={<Zap className="w-5 h-5 text-purple-500" />}
        title="Innovation Leader"
        description="Pushes boundaries with cutting-edge AI capabilities"
      />
      <FeatureItem 
        icon={<CheckCircle className="w-5 h-5 text-green-500" />}
        title="Expert Verified"
        description="Thoroughly tested and validated by our AI specialists"
      />
    </div>
  </div>
);

// Extracted FeatureItem component
interface FeatureItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, title, description }) => (
  <div className="flex items-start gap-3">
    {icon}
    <div>
      <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-1">{title}</h3>
      <p className="text-slate-600 dark:text-slate-400 text-sm">{description}</p>
    </div>
  </div>
);

// Extracted LongDescriptionSection component
interface LongDescriptionSectionProps {
  tool: Tool;
}

const LongDescriptionSection: React.FC<LongDescriptionSectionProps> = ({ tool }) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-8">
    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Zap className="w-5 h-5 text-purple-500" />
      About {tool.title}
    </h2>
    <div 
      className="prose dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: tool.longDescription || '' }}
    />
  </div>
);

// Extracted KeyBenefitsSection component
const KeyBenefitsSection: React.FC = () => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-8">
    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Rocket className="w-5 h-5 text-purple-500" />
      Key Benefits
    </h2>
    <div className="grid md:grid-cols-2 gap-4">
      <FeatureItem 
        icon={<CheckCircle className="w-5 h-5 text-green-500" />}
        title="Time Savings"
        description="Automate repetitive tasks and boost productivity"
      />
      <FeatureItem 
        icon={<Shield className="w-5 h-5 text-blue-500" />}
        title="Reliable Performance"
        description="Consistent results with minimal downtime"
      />
      <FeatureItem 
        icon={<Clock className="w-5 h-5 text-purple-500" />}
        title="Quick Setup"
        description="Get started in minutes with intuitive interface"
      />
      <FeatureItem 
        icon={<Users className="w-5 h-5 text-green-500" />}
        title="Great Support"
        description="Responsive customer service and documentation"
      />
    </div>
  </div>
);

// Extracted TagsSection component
interface TagsSectionProps {
  tags: string[];
}

const TagsSection: React.FC<TagsSectionProps> = ({ tags }) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-8">
    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6 flex items-center gap-3">
      <Tag className="w-5 h-5 text-purple-500" />
      Categories & Features
    </h2>
    <div className="flex flex-wrap gap-3">
      {tags.map((tag, index) => (
        <span
          key={index}
          className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-800 dark:text-purple-200 px-4 py-2 rounded-full text-sm font-medium border border-purple-200 dark:border-purple-800"
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

// Extracted PremiumCTACard component
interface PremiumCTACardProps {
  tool: Tool;
}

const PremiumCTACard: React.FC<PremiumCTACardProps> = ({ tool }) => (
  <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-3xl p-6 text-white shadow-2xl">
    <div className="flex items-center gap-2 mb-3">
      <Crown className="w-5 h-5" />
      <span className="font-bold text-lg">Featured Pick</span>
    </div>
    <p className="text-white/90 text-sm mb-6">
      Our top recommendation for this category. Trusted by thousands of users.
    </p>
    {tool.toolUrl && (
      <a
        href={tool.toolUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full inline-flex items-center justify-center gap-3 bg-white text-purple-600 font-bold py-4 px-6 rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        Try {tool.title}
        <ExternalLink className="w-5 h-5" />
      </a>
    )}
  </div>
);

// Extracted ToolStatsCard component
interface ToolStatsCardProps {
  tool: Tool;
}

const ToolStatsCard: React.FC<ToolStatsCardProps> = ({ tool }) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6">
    <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-3">
      <TrendingUp className="w-5 h-5 text-purple-500" />
      Tool Highlights
    </h3>
    <div className="space-y-3">
      <StatItem label="Status" value={TOOL_STATS.status} icon={<CheckCircle className="w-4 h-4" />} />
      <StatItem 
        label="Pricing" 
        value={tool.isFree ? TOOL_STATS.pricing.free : TOOL_STATS.pricing.paid}
        color={tool.isFree ? "green" : "blue"}
      />
      <StatItem label="Expert Rating" value={TOOL_STATS.rating} icon={<Star className="w-4 h-4 fill-current" />} />
      <StatItem label="User Reviews" value={TOOL_STATS.reviews} />
    </div>
  </div>
);

// Extracted StatItem component
interface StatItemProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
  color?: 'purple' | 'green' | 'blue';
}

const StatItem: React.FC<StatItemProps> = ({ label, value, icon, color = 'purple' }) => {
  const colorClasses = {
    purple: 'text-purple-600 dark:text-purple-400',
    green: 'text-green-600 dark:text-green-400',
    blue: 'text-blue-600 dark:text-blue-400'
  };

  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-600 dark:text-slate-400 text-sm">{label}</span>
      <span className={`flex items-center gap-1 text-sm font-medium ${colorClasses[color]}`}>
        {icon}
        {value}
      </span>
    </div>
  );
};

// Extracted NavigationCards component
const NavigationCards: React.FC = () => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6">
    <Link
      to="/tools/featured"
      className="w-full inline-flex items-center justify-center gap-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-semibold py-3 px-6 rounded-xl transition-all duration-200 mb-3"
    >
      <ArrowLeft className="w-4 h-4" />
      More Featured Tools
    </Link>
    <Link
      to="/tools"
      className="w-full inline-flex items-center justify-center gap-3 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
    >
      Browse All Tools
    </Link>
  </div>
);

export default React.memo(FeaturedToolDetailPage);